export type MobileApiFailureCode = 'network_unavailable' | 'request_timeout' | 'non_json_response';

export type MobileApiFailureEvidence = Readonly<{
  code: MobileApiFailureCode;
  supportReference: string;
  status?: number;
}>;

export class MobileApiFailure extends Error {
  readonly code: MobileApiFailureCode;
  readonly retryable = true;
  readonly supportReference: string;
  readonly status: number | undefined;
  readonly userMessage: string;

  constructor(
    code: MobileApiFailureCode,
    message: string,
    supportReference: string,
    status?: number,
  ) {
    super(`${message} Reference: ${supportReference}`);
    this.name = 'MobileApiFailure';
    this.code = code;
    this.supportReference = supportReference;
    this.status = status;
    this.userMessage = message;
  }
}

export const MOBILE_API_TIMEOUT_MS = 15_000;

export function createBoundedMobileFetch(
  options: {
    fetch?: typeof globalThis.fetch;
    timeoutMs?: number;
    correlationIdFactory?: () => string;
    onFailure?: (evidence: MobileApiFailureEvidence) => void;
  } = {},
): typeof globalThis.fetch {
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  const timeoutMs = options.timeoutMs ?? MOBILE_API_TIMEOUT_MS;
  const correlationIdFactory = options.correlationIdFactory ?? createMobileCorrelationId;
  if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1) {
    throw new Error('Mobile API timeout must be a positive integer');
  }

  return async (input, init) => {
    const original = new Request(input, init);
    const headers = new Headers(original.headers);
    const suppliedReference = headers.get('x-correlation-id');
    const supportReference =
      suppliedReference && SAFE_CORRELATION_ID.test(suppliedReference)
        ? suppliedReference
        : correlationIdFactory();
    headers.set('x-correlation-id', supportReference);
    const correlated = new Request(original, { headers });
    const controller = new AbortController();
    let rejectCallerAbort: ((reason?: unknown) => void) | undefined;
    const callerAbort = new Promise<never>((_, reject) => {
      rejectCallerAbort = reject;
    });
    const forwardAbort = () => {
      controller.abort(original.signal.reason);
      rejectCallerAbort?.(
        correlated.signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'),
      );
    };
    if (correlated.signal.aborted) forwardAbort();
    else correlated.signal.addEventListener('abort', forwardAbort, { once: true });

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const timeoutFailure = new Promise<never>((_, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(
          new MobileApiFailure(
            'request_timeout',
            'The request timed out. Check your connection and try again.',
            supportReference,
          ),
        );
      }, timeoutMs);
    });

    try {
      const response = await Promise.race([
        fetchImplementation(new Request(correlated, { signal: controller.signal })),
        timeoutFailure,
        callerAbort,
      ]);
      if (!response.ok) return await classifyApiFailure(response, supportReference);
      return response;
    } catch (caught) {
      if (caught instanceof MobileApiFailure) {
        reportFailure(options.onFailure, {
          code: caught.code,
          supportReference: caught.supportReference,
          ...(caught.status === undefined ? {} : { status: caught.status }),
        });
        throw caught;
      }
      if (correlated.signal.aborted) throw caught;
      const failure = new MobileApiFailure(
        'network_unavailable',
        'The service could not be reached. Check your connection and try again.',
        supportReference,
      );
      reportFailure(options.onFailure, { code: failure.code, supportReference });
      throw failure;
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
      correlated.signal.removeEventListener('abort', forwardAbort);
    }
  };
}

const SAFE_CORRELATION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createMobileCorrelationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (generated && SAFE_CORRELATION_ID.test(generated)) return generated;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });
}

function reportFailure(
  reporter: ((evidence: MobileApiFailureEvidence) => void) | undefined,
  evidence: MobileApiFailureEvidence,
): void {
  try {
    reporter?.(evidence);
  } catch {
    // Operational reporting is best-effort and cannot replace the application failure.
  }
}

async function classifyApiFailure(response: Response, clientReference: string): Promise<Response> {
  const responseReference =
    response.headers.get('x-correlation-id') ?? response.headers.get('x-request-id');
  const supportReference =
    responseReference && SAFE_CORRELATION_ID.test(responseReference)
      ? responseReference
      : clientReference;
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (!contentType.includes('json')) {
    throw new MobileApiFailure(
      'non_json_response',
      'The service returned an unreadable response. Try again.',
      supportReference,
      response.status,
    );
  }
  try {
    const body: unknown = await response.clone().json();
    if (!body || typeof body !== 'object' || Array.isArray(body)) return response;
    const annotated = { ...body, support_reference: supportReference } as Record<string, unknown>;
    if (typeof annotated.detail === 'string') {
      annotated.detail = `${annotated.detail} Reference: ${supportReference}`;
    }
    const headers = new Headers(response.headers);
    headers.delete('content-length');
    return new Response(JSON.stringify(annotated), {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  } catch {
    throw new MobileApiFailure(
      'non_json_response',
      'The service returned an unreadable response. Try again.',
      supportReference,
      response.status,
    );
  }
}
